import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterPanel, { type FilterGroupConfig } from "@/components/feature/FilterPanel";
import SortBar from "@/components/feature/SortBar";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import { categoryMeta } from "@/mocks/categories";
import type { ContentCategory, SortKey, VideoFilters } from "@/types/video";
import {
  countActiveFilters,
  emptyFilters,
  filterVideos,
  getByCategory,
  getFilterOptions,
  sortVideos,
  yearOptions,
} from "@/utils/video";

const PAGE_SIZE = 10;

function buildGroups(category: ContentCategory): FilterGroupConfig[] {
  const opts = getFilterOptions(category);
  if (category === "movie") {
    return [
      { key: "genres", label: "类型", options: opts.genres },
      { key: "yearRange", label: "年份", options: [] },
      { key: "regions", label: "地区", options: opts.regions },
      { key: "tags", label: "热门标签", options: opts.tags },
    ];
  }
  if (category === "tv") {
    return [
      { key: "genres", label: "类型", options: opts.genres },
      { key: "yearRange", label: "年份", options: [] },
      { key: "regions", label: "地区", options: opts.regions },
      { key: "status", label: "更新状态", options: opts.statuses },
    ];
  }
  return [
    { key: "genres", label: "节目类型", options: opts.genres },
    { key: "platforms", label: "播出平台", options: opts.platforms },
    { key: "yearRange", label: "年份", options: [] },
  ];
}

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const category = (type as ContentCategory) ?? "movie";
  const meta = categoryMeta[category];

  const [filters, setFilters] = useState<VideoFilters>(emptyFilters);
  const [sortKey, setSortKey] = useState<SortKey>("popular");
  const [page, setPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [viewMode, setViewMode] = useState<"page" | "more">("page");
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setFilters(emptyFilters);
    setSortKey("popular");
    setPage(1);
    setVisibleCount(PAGE_SIZE);
    setDrawerOpen(false);
  }, [category]);

  useEffect(() => {
    setPage(1);
    setVisibleCount(PAGE_SIZE);
  }, [filters, sortKey, viewMode]);

  const groups = useMemo(() => (meta ? buildGroups(category) : []), [category, meta]);

  const allItems = useMemo(() => (meta ? getByCategory(category) : []), [category, meta]);

  const result = useMemo(() => {
    const filtered = filterVideos(allItems, filters);
    return sortVideos(filtered, sortKey);
  }, [allItems, filters, sortKey]);

  if (!meta) {
    return (
      <SiteLayout>
        <div className="mx-auto max-w-[1400px] px-4 py-20 md:px-6">
          <EmptyState
            icon="ri-error-warning-line"
            title="分类不存在"
            description="你访问的分类页面不存在，请返回首页重新选择。"
          >
            <Link to="/" className="cursor-pointer rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50">
              返回首页
            </Link>
          </EmptyState>
        </div>
      </SiteLayout>
    );
  }

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const shownItems =
    viewMode === "page"
      ? result.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
      : result.slice(0, visibleCount);
  const activeCount = countActiveFilters(filters);

  const sidebarPanel = (
    <FilterPanel
      groups={groups}
      filters={filters}
      yearOptions={yearOptions}
      onChange={setFilters}
      onReset={() => setFilters(emptyFilters)}
    />
  );

  return (
    <SiteLayout>
      <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: meta.name }]} />

        <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
          <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            {meta.name}频道
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">{meta.intro}</p>
        </div>

        <div className="mt-6 flex flex-col gap-6 lg:flex-row">
          <aside className="hidden w-64 shrink-0 lg:block">
            <div className="sticky top-20 rounded-lg border border-background-200 bg-background-100 p-5">
              {sidebarPanel}
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="mb-3 flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 lg:hidden"
            >
              <i className="ri-filter-3-line" />
              筛选条件
              {activeCount > 0 && (
                <span className="rounded-full bg-accent-500 px-2 text-xs font-bold text-background-50">{activeCount}</span>
              )}
            </button>

            <SortBar
              sortKey={sortKey}
              onChange={setSortKey}
              total={result.length}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />

            <div className="mt-5">
              {result.length === 0 ? (
                <EmptyState
                  icon="ri-search-eye-line"
                  title="没有符合条件的影视"
                  description="试试调整或重置筛选条件，发现更多精彩内容。"
                >
                  <button
                    type="button"
                    onClick={() => setFilters(emptyFilters)}
                    className="cursor-pointer rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50"
                  >
                    重置筛选
                  </button>
                </EmptyState>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
                    {shownItems.map((item) => (
                      <VideoCard key={item.id} item={item} showFavorite />
                    ))}
                  </div>

                  {viewMode === "page" ? (
                    <Pagination
                      page={page}
                      totalPages={totalPages}
                      onChange={(next) => {
                        setPage(next);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    />
                  ) : (
                    <div className="mt-8 flex justify-center">
                      {visibleCount < result.length ? (
                        <button
                          type="button"
                          onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                          className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
                        >
                          <i className="ri-add-line" />
                          加载更多
                        </button>
                      ) : (
                        <p className="text-sm text-foreground-400">已经到底啦</p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 cursor-pointer bg-primary-950/50"
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[82vh] overflow-y-auto rounded-t-2xl bg-background-100 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-base font-bold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-600 hover:bg-background-200"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            {sidebarPanel}
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-5 w-full cursor-pointer rounded-md bg-primary-600 px-4 py-3 text-sm font-semibold text-background-50"
            >
              查看 {result.length} 个结果
            </button>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}